/**
 * Session templates, trading-day boundaries, and market state.
 *
 * The single most important fact in this file: a CME futures trading day starts
 * at 17:00 America/Chicago on the PREVIOUS calendar day. Sunday evening's open
 * begins Monday's trading day.
 *
 * Any day-boundary logic that reaches for `date_trunc('day')` or
 * `new Date().getDate()` is wrong for every futures contract, and wrong in a way
 * that renders a plausible-looking chart.
 *
 * @see docs/16-instrument-reference.md
 */
import type { Holiday, MarketStatus, SessionTemplate, TimeWindow } from './types.js';

/** Civil date-time parts in a named timezone. */
export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number;
  /** 0 = Sunday .. 6 = Saturday */
  weekday: number;
}

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

/**
 * A formatter for `timeZone`, or one in UTC when the runtime will not make one.
 *
 * `new Intl.DateTimeFormat(…, { timeZone })` throws a `RangeError` for a zone the
 * runtime does not know, and the runtimes do not all know the same zones. Node ships
 * full ICU; a browser has the platform's database; Hermes on a phone has whatever the
 * OS gives it. A zone that works everywhere in development can be rejected on a device.
 *
 * This used to let that throw, from inside `packages/core`, on a code path a screen
 * renders through — which turns an unknown timezone name into a blank screen at best
 * and, in a React Native release build, into `abort()`.
 *
 * `format.ts` in the api-client already decided this exact question the other way:
 * *"an invalid zone falls back rather than throwing — a stored preference from a
 * renamed IANA zone should not blank a screen."* Two modules in one product
 * disagreeing about whether a bad zone is fatal is the defect; this is the side that
 * was wrong.
 *
 * UTC is the fallback rather than the device's zone on purpose. A session boundary
 * computed in the *viewer's* zone would be wrong by a whole number of hours and look
 * plausible; computed in UTC it is wrong in a way that is obvious, consistent between
 * devices, and matches what the instrument master means by an unspecified zone.
 */
function formatterFor(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    const opts: Intl.DateTimeFormatOptions = {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', weekday: 'short',
      hour12: false,
    };
    try {
      f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone });
    } catch {
      f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });
    }
    // Cached either way, including the fallback: a zone the runtime rejects
    // rejects it every time, and retrying the constructor per call would put a
    // throw-and-catch on a path that runs for every bar on a chart.
    formatterCache.set(timeZone, f);
  }
  return f;
}

/**
 * Break a UTC instant into civil parts in `timeZone`.
 *
 * Uses Intl rather than manual offset arithmetic so DST transitions are handled
 * by the platform's tz database rather than by us.
 */
export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts = formatterFor(timeZone).formatToParts(date);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  // Intl renders midnight as hour 24 in some ICU versions; normalise it.
  const rawHour = Number(get('hour'));
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: rawHour === 24 ? 0 : rawHour,
    minute: Number(get('minute')),
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 0,
  };
}

/** `HH:MM` -> minutes since local midnight. */
export function toMinutes(hhmm: string): number {
  const [h = '0', m = '0'] = hhmm.split(':');
  return Number(h) * 60 + Number(m);
}

function minutesOf(p: ZonedParts): number {
  return p.hour * 60 + p.minute;
}

/** `YYYY-MM-DD` for the given civil date. */
export function isoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Shift a civil `YYYY-MM-DD` by whole days. Calendar arithmetic only. */
export function addDays(iso: string, days: number): string {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d) + days * 86_400_000;
  const dt = new Date(t);
  return isoDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

/** Day of week for a civil `YYYY-MM-DD`. 0 = Sunday. */
export function weekdayOf(iso: string): number {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * The trading day a timestamp belongs to, as `YYYY-MM-DD`.
 *
 * At or after the session start time (17:00 CT), the instant belongs to the
 * NEXT calendar day's trading session.
 */
export function tradingDayFor(template: SessionTemplate, at: Date): string {
  const p = zonedParts(at, template.timezone);
  const today = isoDate(p.year, p.month, p.day);
  return minutesOf(p) >= toMinutes(template.tradingDayStart) ? addDays(today, 1) : today;
}

function inWindow(minutes: number, w: TimeWindow): boolean {
  const s = toMinutes(w.start);
  const e = toMinutes(w.end);
  // A window that wraps past midnight (start > end) covers both tails.
  return s <= e ? minutes >= s && minutes < e : minutes >= s || minutes < e;
}

/**
 * Full market state for an instant.
 *
 * Returns a distinct state for weekend, holiday, early close, and maintenance
 * break. The UI must be able to tell the user *why* the market is closed —
 * conflating "closed" with "we lost the feed" trains people to ignore genuine
 * connection warnings.
 */
export function marketStatus(
  template: SessionTemplate,
  holidays: readonly Holiday[],
  at: Date,
): MarketStatus {
  const p = zonedParts(at, template.timezone);
  const mins = minutesOf(p);
  const startM = toMinutes(template.tradingDayStart);
  const endM = toMinutes(template.tradingDayEnd);
  const tradingDay = tradingDayFor(template, at);

  // --- weekend -------------------------------------------------------------
  // The week runs Sunday 17:00 CT to Friday 16:00 CT.
  const closedForWeekend =
    p.weekday === 6 ||
    (p.weekday === 5 && mins >= endM) ||
    (p.weekday === 0 && mins < startM);
  if (closedForWeekend) {
    return {
      state: 'closed_weekend', isOpen: false, tradingDay,
      reason: 'Market closed for the weekend. Globex reopens Sunday 17:00 CT.',
    };
  }

  // --- holiday -------------------------------------------------------------
  const holiday = holidays.find((h) => h.date === tradingDay);
  if (holiday?.kind === 'closed') {
    return {
      state: 'closed_holiday', isOpen: false, tradingDay,
      reason: `Market closed — ${holiday.name}.`,
    };
  }
  if (holiday?.kind === 'early_close' && holiday.closesAt) {
    const closeM = toMinutes(holiday.closesAt);
    // Early closes apply to the day-session portion, after the 17:00 rollover.
    if (mins >= closeM && mins < startM) {
      return {
        state: 'closed_early', isOpen: false, tradingDay,
        reason: `Market closed early — ${holiday.name} (${holiday.closesAt} CT).`,
      };
    }
  }

  // --- daily maintenance break --------------------------------------------
  for (const w of template.dailyBreaks) {
    if (inWindow(mins, w)) {
      return {
        state: 'maintenance_break', isOpen: false, tradingDay,
        reason: `Daily maintenance break (${w.start}–${w.end} CT). This is not a connection problem.`,
      };
    }
  }

  return { state: 'open', isOpen: true, tradingDay, reason: 'Market open.' };
}

export function isMarketOpen(
  template: SessionTemplate,
  holidays: readonly Holiday[],
  at: Date,
): boolean {
  return marketStatus(template, holidays, at).isOpen;
}

/** True when the instant falls inside regular trading hours. */
export function isRth(template: SessionTemplate, at: Date): boolean {
  const p = zonedParts(at, template.timezone);
  return inWindow(minutesOf(p), template.rth);
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

/**
 * CME equity index (ES, NQ, YM, RTY and their micros).
 * Sun–Fri 17:00–16:00 CT with a 60-minute break beginning 16:00 CT.
 */
export const CME_EQUITY_INDEX: SessionTemplate = {
  id: 'cme_equity_index',
  timezone: 'America/Chicago',
  tradingDayStart: '17:00',
  tradingDayEnd: '16:00',
  dailyBreaks: [{ start: '16:00', end: '17:00' }],
  rth: { start: '08:30', end: '15:15' },
  holidayCalendarId: 'us_futures',
};

/** NYMEX/COMEX energy and metals. Same 17:00 rollover, 16:00 close. */
export const CME_ENERGY_METALS: SessionTemplate = {
  id: 'cme_energy_metals',
  timezone: 'America/Chicago',
  tradingDayStart: '17:00',
  tradingDayEnd: '16:00',
  dailyBreaks: [{ start: '16:00', end: '17:00' }],
  rth: { start: '08:00', end: '13:30' },
  holidayCalendarId: 'us_futures',
};

/** CBOT interest rate futures. */
export const CBOT_RATES: SessionTemplate = {
  id: 'cbot_rates',
  timezone: 'America/Chicago',
  tradingDayStart: '17:00',
  tradingDayEnd: '16:00',
  dailyBreaks: [{ start: '16:00', end: '17:00' }],
  rth: { start: '07:20', end: '14:00' },
  holidayCalendarId: 'us_futures',
};

/** CME FX futures. */
export const CME_FX: SessionTemplate = {
  id: 'cme_fx',
  timezone: 'America/Chicago',
  tradingDayStart: '17:00',
  tradingDayEnd: '16:00',
  dailyBreaks: [{ start: '16:00', end: '17:00' }],
  rth: { start: '07:20', end: '14:00' },
  holidayCalendarId: 'us_futures',
};

/**
 * US cash equities and ETFs.
 *
 * The template that does NOT share the futures shape, and the difference is the
 * point: a stock's trading day starts at midnight local, not at 17:00 the
 * previous day. Bucketing AAPL on a CME trading day would put the whole
 * afternoon on the wrong date.
 */
export const US_EQUITY: SessionTemplate = {
  id: 'us_equity',
  timezone: 'America/New_York',
  tradingDayStart: '00:00',
  tradingDayEnd: '23:59',
  // Pre- and post-market are continuous with RTH on the feeds we consume, so
  // there is no halt to model. RTH below is what the session toggle means here.
  dailyBreaks: [],
  rth: { start: '09:30', end: '16:00' },
  holidayCalendarId: 'us_equities',
};

/**
 * Crypto. Never closes, so every field that describes a boundary is degenerate.
 *
 * Kept as a real template rather than a null, because every consumer resolves a
 * template to find a timezone and a trading-day boundary, and a special case
 * for "no session" would have to be handled at each of them.
 */
export const ALWAYS_OPEN: SessionTemplate = {
  id: 'always_open',
  // UTC on purpose. There is no exchange whose local time is the right answer,
  // and a daily candle that shifts with the viewer is not comparable.
  timezone: 'UTC',
  tradingDayStart: '00:00',
  tradingDayEnd: '23:59',
  dailyBreaks: [],
  rth: { start: '00:00', end: '23:59' },
  holidayCalendarId: 'none',
};

/**
 * Spot forex. Opens Sunday 17:00 New York, closes Friday 17:00.
 *
 * The 17:00 New York rollover is the market's own convention and is what
 * brokers charge swap against, so a daily bar cut anywhere else disagrees with
 * the trader's statement.
 */
export const FX_SPOT: SessionTemplate = {
  id: 'fx_spot',
  timezone: 'America/New_York',
  tradingDayStart: '17:00',
  tradingDayEnd: '17:00',
  dailyBreaks: [],
  // No regular hours exist in a 24-hour market. The London/New York overlap is
  // where the liquidity is, and it is the honest answer to "RTH".
  rth: { start: '08:00', end: '12:00' },
  holidayCalendarId: 'none',
};

/**
 * CBOT grains and oilseeds — ZC, ZS, ZW, ZL, ZM, ZO.
 *
 * The one futures schedule that does NOT look like the others, and the reason
 * this template exists rather than reusing `cbot_rates`: grains trade a short
 * overnight, then close for forty-five minutes, then run a day session that
 * ends at 13:20 rather than 16:00. Putting a corn trade on a rates calendar
 * would file half the overnight session on the wrong trading day.
 */
export const CBOT_GRAINS: SessionTemplate = {
  id: 'cbot_grains',
  timezone: 'America/Chicago',
  tradingDayStart: '19:00',
  tradingDayEnd: '13:20',
  dailyBreaks: [{ start: '07:45', end: '08:30' }],
  rth: { start: '08:30', end: '13:20' },
  holidayCalendarId: 'us_futures',
};

/**
 * CME livestock — LE, HE, GF.
 *
 * Day-session only, and short. No overnight at all, which makes it the one
 * template where "the trading day" and "the day session" are the same thing.
 */
export const CME_LIVESTOCK: SessionTemplate = {
  id: 'cme_livestock',
  timezone: 'America/Chicago',
  tradingDayStart: '08:30',
  tradingDayEnd: '13:05',
  dailyBreaks: [],
  rth: { start: '08:30', end: '13:05' },
  holidayCalendarId: 'us_futures',
};

/**
 * ICE US softs — KC, SB, CC, CT, OJ.
 *
 * New York hours rather than Chicago, and each contract's window differs by up
 * to an hour. The template takes the widest of them; a trade that lands in a
 * contract's own gap is still filed on the right trading day, which is what
 * this is for.
 */
export const ICE_SOFTS: SessionTemplate = {
  id: 'ice_softs',
  timezone: 'America/New_York',
  tradingDayStart: '20:00',
  tradingDayEnd: '14:30',
  dailyBreaks: [],
  rth: { start: '09:00', end: '14:30' },
  holidayCalendarId: 'us_futures',
};

/**
 * Cboe Futures Exchange — VX.
 *
 * Nearly 24 hours with a short maintenance break, and it settles against a
 * morning auction rather than a close.
 */
export const CFE_VOLATILITY: SessionTemplate = {
  id: 'cfe_volatility',
  timezone: 'America/Chicago',
  tradingDayStart: '17:00',
  tradingDayEnd: '15:15',
  dailyBreaks: [{ start: '15:15', end: '17:00' }],
  rth: { start: '08:30', end: '15:15' },
  holidayCalendarId: 'us_futures',
};

export const SESSION_TEMPLATES: Readonly<Record<string, SessionTemplate>> = {
  [CME_EQUITY_INDEX.id]: CME_EQUITY_INDEX,
  [CME_ENERGY_METALS.id]: CME_ENERGY_METALS,
  [CBOT_RATES.id]: CBOT_RATES,
  [CME_FX.id]: CME_FX,
  [US_EQUITY.id]: US_EQUITY,
  [ALWAYS_OPEN.id]: ALWAYS_OPEN,
  [FX_SPOT.id]: FX_SPOT,
  [CBOT_GRAINS.id]: CBOT_GRAINS,
  [CME_LIVESTOCK.id]: CME_LIVESTOCK,
  [ICE_SOFTS.id]: ICE_SOFTS,
  [CFE_VOLATILITY.id]: CFE_VOLATILITY,
};

export function sessionTemplate(id: string): SessionTemplate {
  const t = SESSION_TEMPLATES[id];
  if (!t) throw new Error(`Unknown session template: ${id}`);
  return t;
}

/**
 * US futures holiday calendar.
 *
 * Verify against CME's published calendar before each year — these are
 * data, not truth, and CME occasionally adjusts an early close.
 */
export const US_FUTURES_HOLIDAYS_2026: readonly Holiday[] = [
  { date: '2026-01-01', name: "New Year's Day", kind: 'closed' },
  { date: '2026-01-19', name: 'Martin Luther King Jr. Day', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-02-16', name: "Presidents' Day", kind: 'early_close', closesAt: '12:00' },
  { date: '2026-04-03', name: 'Good Friday', kind: 'closed' },
  { date: '2026-05-25', name: 'Memorial Day', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-06-19', name: 'Juneteenth', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-07-03', name: 'Independence Day (observed)', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-09-07', name: 'Labor Day', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-11-26', name: 'Thanksgiving', kind: 'closed' },
  { date: '2026-11-27', name: 'Day after Thanksgiving', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-12-24', name: 'Christmas Eve', kind: 'early_close', closesAt: '12:00' },
  { date: '2026-12-25', name: 'Christmas Day', kind: 'closed' },
];

export const HOLIDAY_CALENDARS: Readonly<Record<string, readonly Holiday[]>> = {
  us_futures: US_FUTURES_HOLIDAYS_2026,
};

export function holidayCalendar(id: string): readonly Holiday[] {
  return HOLIDAY_CALENDARS[id] ?? [];
}
